/* Emparejar lo que Whisper escuchó en una grabación con las palabras del
   texto, para saber en qué segundo empieza cada una (el karaoke de las
   páginas grabadas).

   No alcanza con ir 1 a 1: quien lee se equivoca, repite, agrega un "y" o se
   saltea una palabra, y Whisper a veces junta o parte palabras. Es la
   alineación clásica de dos secuencias (Needleman-Wunsch): premia pares
   parecidos y cobra por saltearse una palabra de cualquiera de los lados. Las
   palabras del texto que quedan sin par toman un tiempo interpolado entre sus
   vecinas emparejadas. */

/** [palabra, inicio en s, fin en s], como lo guarda functions/api/alignAudio.js. */
export type HeardWord = [word: string, start: number, end: number];

const GAP = -0.5;
/** Debajo de esta proporción de palabras emparejadas, la grabación no es de
 *  este texto (o Whisper no entendió nada): mejor no inventar tiempos. */
const MIN_MATCHED = 0.4;

function clean(w: string): string {
  return w
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/** Distancia de edición, para puntuar palabras parecidas (Whisper escribe
 *  "Clarisa" y el cuento "Clarissa"). */
function levenshtein(a: string, b: string): number {
  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}

function similarity(a: string, b: string): number {
  if (!a || !b) return -1;
  if (a === b) return 2;
  const ratio = 1 - levenshtein(a, b) / Math.max(a.length, b.length);
  return ratio >= 0.6 ? ratio * 1.5 : -1;
}

/**
 * Segundo de inicio de cada palabra de `textWords` dentro de la grabación, o
 * null si la grabación no se parece al texto.
 */
export function alignWordTimes(textWords: string[], heard: HeardWord[]): number[] | null {
  const n = textWords.length;
  const m = heard.length;
  if (!n || !m) return null;

  const a = textWords.map(clean);
  const b = heard.map((h) => clean(h[0]));

  // score[i][j]: mejor puntaje alineando a[0..i) con b[0..j).
  const score: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  const move: Uint8Array[] = Array.from({ length: n + 1 }, () => new Uint8Array(m + 1)); // 1 diag, 2 arriba, 3 izq
  for (let i = 1; i <= n; i++) { score[i][0] = i * GAP; move[i][0] = 2; }
  for (let j = 1; j <= m; j++) { score[0][j] = j * GAP; move[0][j] = 3; }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const d = score[i - 1][j - 1] + similarity(a[i - 1], b[j - 1]);
      const up = score[i - 1][j] + GAP;
      const left = score[i][j - 1] + GAP;
      if (d >= up && d >= left) { score[i][j] = d; move[i][j] = 1; }
      else if (up >= left) { score[i][j] = up; move[i][j] = 2; }
      else { score[i][j] = left; move[i][j] = 3; }
    }
  }

  const times: (number | null)[] = new Array(n).fill(null);
  let matched = 0;
  for (let i = n, j = m; i > 0 || j > 0;) {
    const mv = move[i][j];
    if (mv === 1) {
      if (similarity(a[i - 1], b[j - 1]) > 0) { times[i - 1] = heard[j - 1][1]; matched++; }
      i--; j--;
    } else if (mv === 2) i--;
    else j--;
  }
  if (matched / n < MIN_MATCHED) return null;

  // Las que quedaron sin par: repartidas entre sus vecinas emparejadas. Al
  // principio y al final, contra el primer y el último tiempo que se oyó.
  const out = new Array<number>(n);
  const firstHeard = heard[0][1];
  const lastHeard = heard[m - 1][2];
  let i = 0;
  while (i < n) {
    if (times[i] !== null) { out[i] = times[i]!; i++; continue; }
    const from = i;
    while (i < n && times[i] === null) i++;
    const before = from > 0 ? out[from - 1] : firstHeard;
    const after = i < n ? times[i]! : lastHeard;
    const steps = i - from + 1;
    for (let k = from; k < i; k++) out[k] = before + ((after - before) * (k - from + (from > 0 ? 1 : 0))) / steps;
  }
  // Nunca hacia atrás: el resaltado solo avanza.
  for (let k = 1; k < n; k++) if (out[k] < out[k - 1]) out[k] = out[k - 1];
  return out;
}

/**
 * Tiempos estimados cuando no hay alineación: la duración repartida según el
 * largo de cada palabra (una palabra larga tarda más en decirse). Se desfasa
 * con las pausas, pero es mejor que no resaltar nada.
 */
export function estimateWordTimes(textWords: string[], durationSec: number): number[] {
  const weights = textWords.map((w) => clean(w).length + 2);
  const total = weights.reduce((s, w) => s + w, 0) || 1;
  const out: number[] = [];
  let acc = 0;
  for (const w of weights) {
    out.push((acc / total) * durationSec);
    acc += w;
  }
  return out;
}
